<template>
  <div ref="breadcrumbs" class="sf-row sf-below-0">
    <div class="sf-col">
      <DocumentBreadcrumbs
        :parent-category="parentCategory"
        :permanent-category="permanentCategory"
        :navigate-function="entryClick"
        :navigate-params="{
          id: parentCategory ? parentCategory.id : null,
          category: true,
        }"
      />
    </div>
  </div>

  <div ref="scrollContent" class="sf-row">
    <div
      class="sf-col tw-overflow-y-auto"
      :style="{
        maxHeight: forSidebar
          ? `calc(100vh - var(--sticky-header-height) - var(--system-banner-height) - var(--main-subheader-height) - ${maxHeightOffset}px)`
          : '378px',
      }"
    >
      <div
        v-if="entries.length === 0 && !searchQuery"
        class="sf-alignc sf-c-text sf-bg-snow-dark sf-font-l sf-pad-4 sf-dim-el"
      >
        {{ I18n.t('permanent_document.modal_select.empty_folder') }}
      </div>
      <div
        v-else
        class="
          tw-rounded-lg
          tw-overflow-hidden
          tw-border-solid
          tw-border
          tw-border-sf-neutral-lighter-2
        "
      >
        <table class="sf-table sf-table-border sf-table-middle tw-shadow-none">
          <thead>
            <tr>
              <th class="!tw-px-4 !tw-py-3 !tw-border-none">
                <div class="sf-level sf-level-between">
                  <div class="sf-level-item">
                    {{
                      I18n.t(
                        'components.document.existing_docs_modal.document_header'
                      )
                    }}
                  </div>
                  <div v-if="hasAnyAttachments" class="sf-level-item">
                    {{
                      I18n.t(
                        'components.document.existing_docs_modal.attachments'
                      )
                    }}
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td
                class="
                  !tw-border-l-0
                  !tw-border-r-0
                  !tw-border-b-0
                  !tw-border-sf-neutral-lighter-2
                  tw-group
                "
                :class="tdClasses(entry)"
                :data-testid="`document-entry-${entry.title}-${
                  selectedEntriesIds.includes(entry.id)
                    ? 'selected'
                    : 'unselected'
                }`"
                @click="entryClick(entry)"
              >
                <div class="sf-level sf-level-between tw-relative">
                  <div class="sf-level-item">
                    <div class="sf-level sf-level-small">
                      <span :class="entry.icon">
                        <span
                          v-if="entry.invisible"
                          class="
                            fa fa-eye-slash
                            documents-document-icon
                            documents-lock-icon
                            documents-eye-slash-icon-overlay
                            document-selector-eye-slash-icon-overlay
                          "
                        />
                      </span>
                      <div
                        v-dompurify-html="entry.title"
                        class="sf-break-all"
                      />
                      <span v-if="entry.count" class="sf-dim-el">
                        ({{ itemsTotal(entry) }})
                      </span>
                    </div>
                  </div>
                  <div v-if="entry.attachments" class="sf-level-item">
                    <span class="sf-font-xs sf-c-text-light sf-pad-r-1">
                      {{ entry.attachments.length }}
                    </span>
                    <span
                      class="sf-c-text-light fa fa-info-circle"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="attachmentsList(entry)"
                    />
                  </div>
                  <button
                    v-if="showViewButton && !entry.category"
                    type="button"
                    class="
                      -tw-right-3
                      tw-absolute tw-top-1/2
                      -tw-translate-y-1/2
                      tw-opacity-0
                      group-hover:tw-opacity-100
                      tw-transition-opacity
                      tw-duration-200
                      tw-bg-transparent
                      tw-text-sf-neutral-dark-1
                      hover:tw-text-sf-primary
                      tw-border-none tw-outline-none tw-cursor-pointer
                    "
                    :title="
                      I18n.t(
                        'components.markdown_editor.document_link_picker.view_document'
                      )
                    "
                    @click.stop="$emit('view-document', entry)"
                  >
                    <i class="fa fa-external-link-alt tw-text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentBreadcrumbs from '@app/application/document/sections/document-breadcrumbs'

import useI18n from '@app/shared/vue/composables/use-I18n'
import { nextTick } from 'vue'

export default {
  name: 'ExistingDocumentsTable',

  components: {
    DocumentBreadcrumbs,
  },

  props: {
    entries: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedEntriesIds: {
      type: Array,
      required: true,
      default: () => [],
    },
    permanentCategory: {
      type: Object,
      default: () => {},
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    scrollOffset: {
      type: Number,
      required: true,
    },
    forSidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
    showViewButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['navigate', 'toggle-selection', 'view-document'],

  setup() {
    const I18n = useI18n()

    return {
      I18n,
    }
  },

  data() {
    return {
      maxHeightOffset: 0,
    }
  },

  computed: {
    parentCategory() {
      return this.permanentCategory && this.permanentCategory.parentCategory
    },

    hasAnyAttachments() {
      return this.entries.some((entry) => entry.attachments?.length > 0)
    },
  },

  watch: {
    scrollOffset() {
      nextTick(() => {
        this.calculateMaxHeightOffset()
      })
    },
  },

  mounted() {
    nextTick(() => {
      this.calculateMaxHeightOffset()
    })
  },

  methods: {
    calculateMaxHeightOffset() {
      const heightOfOtherOutsideElements = 250
      const scrollContentMargin = parseFloat(
        window
          .getComputedStyle(this.$refs.scrollContent)
          .getPropertyValue('margin-bottom')
      )

      this.maxHeightOffset =
        this.scrollOffset +
        heightOfOtherOutsideElements +
        this.$refs.breadcrumbs.offsetHeight +
        scrollContentMargin
    },

    tdClasses(entry) {
      return `${this.entryHighlightingColor(entry)} sf-pointer`
    },

    itemsTotal(entry) {
      let text = `${entry.count} `

      if (entry.count === 1) {
        text += I18n.t('components.document.existing_docs_modal.item')
      } else {
        text += I18n.t('components.document.existing_docs_modal.items')
      }

      return text
    },

    entryHighlightingColor(entry) {
      return this.selectedEntriesIds.includes(entry.id)
        ? 'sf-bg-primary-subtle'
        : ''
    },

    entryClick(entry) {
      if (entry.category) {
        this.$emit('navigate', entry)
      } else {
        this.$emit('toggle-selection', entry)
      }
    },

    attachmentsList(entry) {
      const attachments = entry.attachments.slice(0, 6)

      if (entry.attachments.length > 6) attachments.push('and more ...')

      return attachments.join('\n')
    },

    documentsIconClass(entry) {
      const commonClasses = 'documents-document-icon sf-font-m sf-c-light'

      if (entry.type === 'document') {
        return `i-documents-${this.documentsIconClassForExtension(
          entry.extension
        )} ${commonClasses}`
      } else {
        return `i-documents-folder ${commonClasses}`
      }
    },
    documentsIconClassForExtension(extension) {
      switch (extension) {
        case ('.doc', '.docx', '.rtf', '.dot'):
          return 'file-word'
        case ('.xls', '.xlt', '.xlm', '.xlsx', '.xlsm'):
          return 'file-excel'
        case ('.ppt', '.pptx', '.pptm'):
          return 'file-powerpoint'
        case '.pdf':
          return 'file-pdf'
        default:
          return 'file'
      }
    },
  },
}
</script>
